import React, { useEffect, useRef, useState } from 'react';
import { getChargifyConfig } from '../config/ChargifyConfig';
import './ChargifyPaymentForm.css';

/**
 * ChargifyPaymentForm Component
 * 
 * Handles secure credit card payment processing using Chargify.js
 * Card data never touches our servers - Chargify handles tokenization
 * 
 * @param {Object} props
 * @param {Object} props.product - Selected product to purchase
 * @param {Function} props.onSuccess - Callback with chargify_token on success
 * @param {Function} props.onError - Callback with error message on failure
 * @param {Function} props.onCancel - Callback when user cancels
 * @param {string} props.buttonText - Custom text for submit button (default: "Pay {price}")
 * @param {string} props.processingText - Custom text while processing (default: "Processing...")
 */
const ChargifyPaymentForm = ({ product, onSuccess, onError, onCancel, buttonText, processingText = 'Processing...' }) => {
  const chargifyRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formError, setFormError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    console.log('💳 ChargifyPaymentForm: Initializing Chargify.js...');
    
    // Wait for Chargify.js to load
    const initChargify = () => {
      if (!window.Chargify) {
        console.warn('⚠️ Chargify.js not loaded yet, retrying...');
        setTimeout(initChargify, 100);
        return;
      }

      try {
        // Initialize Chargify with configuration from centralized config
        const chargifyConfig = getChargifyConfig();
        
        if (!chargifyConfig.publicKey || !chargifyConfig.subdomain) {
          throw new Error(`Chargify not configured for this site. Please set REACT_APP_CHARGIFY_PUBLIC_KEY and REACT_APP_CHARGIFY_SUBDOMAIN in .env`);
        }
        
        console.log('💳 ChargifyPaymentForm: Using Chargify config:', {
          site: chargifyConfig.siteName,
          publicKey: chargifyConfig.publicKey.substring(0, 10) + '...',
          serverHost: chargifyConfig.serverHost,
          subdomain: chargifyConfig.subdomain
        });
        
        const chargify = new window.Chargify();
        
        chargify.load({
          publicKey: chargifyConfig.publicKey,
          type: 'card',
          serverHost: chargifyConfig.serverHost,
          
          // Form configuration
          hideCardImage: true,
          optionalLabel: ' (optional)',
          requiredLabel: '*',
          
          // Styling to match ChillFree design
          style: {
            '.cfy-cc-image': {
              height: '25px',
              width: 'auto',
            },
            '.cfy-cc-image img': {
              height: '25px',
              width: 'auto',
              maxHeight: '25px',
              padding: '2px',
            },
            input: {
              fontSize: '15px',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              fontWeight: '400',
              padding: '14px 16px',
              lineHeight: '1.5',
              color: '#ffffff',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
            },
            'input::placeholder': {
              color: 'rgba(255, 255, 255, 0.4)',
              letterSpacing: '0.5px',
            },
            'input:focus': {
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              borderColor: '#56bdaf',
              outline: 'none',
              boxShadow: '0 0 0 3px rgba(86, 189, 175, 0.1)',
            },
            label: {
              fontSize: '13px',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '6px',
              display: 'block',
            },
            message: {
              fontSize: '13px',
              color: '#ef4444',
              marginTop: '6px',
            },
            '.chargify-card-icon': {
              height: '25px',
              maxHeight: '25px',
            },
            'img': {
              maxHeight: '25px',
            },
          },
          
          // Field configuration
          fields: {
            firstName: {
              selector: '#chargify-first-name',
              label: 'First Name',
              placeholder: 'John',
              required: true,
              message: 'First name is required',
            },
            lastName: {
              selector: '#chargify-last-name',
              label: 'Last Name',
              placeholder: 'Doe',
              required: true,
              message: 'Last name is required',
            },
            number: {
              selector: '#chargify-card-number',
              label: 'Card Number',
              placeholder: '•••• •••• •••• ••••',
              required: true,
              message: 'Card number is required',
            },
            month: {
              selector: '#chargify-month',
              label: 'Expiration Month',
              placeholder: 'MM',
              required: true,
              message: 'Month is required',
            },
            year: {
              selector: '#chargify-year',
              label: 'Expiration Year',
              placeholder: 'YYYY',
              required: true,
              message: 'Year is required',
            },
            cvv: {
              selector: '#chargify-cvv',
              label: 'CVV',
              placeholder: '•••',
              required: true,
              message: 'CVV is required',
            },
            zip: {
              selector: '#chargify-zip',
              label: 'Billing ZIP Code',
              placeholder: '12345',
              required: true,
              message: 'ZIP code is required',
            },
          },
        });

        chargifyRef.current = chargify;
        setIsLoading(false);
        console.log('✅ ChargifyPaymentForm: Chargify.js initialized successfully');
      } catch (error) {
        console.error('❌ ChargifyPaymentForm: Error initializing Chargify:', error);
        setFormError('Failed to initialize payment form. Please refresh the page.');
        setIsLoading(false);
        onError?.(error.message || 'Failed to initialize payment form');
      }
    };

    initChargify();

    // Cleanup
    return () => {
      if (chargifyRef.current) {
        chargifyRef.current = null;
      }
    };
  }, [onError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!chargifyRef.current) {
      setFormError('Payment form not initialized');
      return;
    }

    setIsProcessing(true);
    setFormError(null);
    console.log('💳 ChargifyPaymentForm: Submitting payment form...');

    try {
      // Tokenize the card data with Chargify
      // Pass the form element as the first argument
      chargifyRef.current.token(
        e.target, // Pass the form element
        // Success callback
        (token) => {
          console.log('✅ ChargifyPaymentForm: Token received, keeping overlay visible...');
          // IMPORTANT: Do NOT set isProcessing to false here!
          // Keep the overlay visible while the parent component processes the payment.
          // The parent will either navigate away (unmounting this component) or show an error.
          onSuccess?.(token);
        },
        // Error callback
        (error) => {
          console.error('❌ ChargifyPaymentForm: Tokenization error:', error);
          // Only hide overlay on error so user can fix and retry
          setIsProcessing(false);
          
          let errorMessage = 'Payment processing failed. Please check your card details.';
          
          if (error.errors && error.errors.length > 0) {
            errorMessage = error.errors.map(e => e.message).join(', ');
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          setFormError(errorMessage);
          onError?.(errorMessage);
        }
      );
    } catch (error) {
      console.error('❌ ChargifyPaymentForm: Unexpected error:', error);
      setIsProcessing(false);
      setFormError('An unexpected error occurred. Please try again.');
      onError?.(error.message || 'An unexpected error occurred');
    }
  };

  if (isLoading) {
    return (
      <div className="chargify-loading">
        <div className="chargify-spinner"></div>
        <p>Loading secure payment form...</p>
      </div>
    );
  }

  return (
    <div className="chargify-payment-form">
      {/* Full-screen processing overlay - blocks all interaction */}
      {isProcessing && (
        <div className="payment-processing-overlay">
          <div className="payment-processing-content">
            <div className="payment-processing-spinner">
              <svg viewBox="0 0 50 50">
                <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </div>
            <h3>Processing Payment</h3>
            <p>Please wait while we securely process your payment...</p>
            <div className="payment-processing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}

      <div className="payment-header">
        <h3>Payment Information</h3>
        <p className="payment-product">
          {product?.name} - <strong>{product?.price}</strong>
        </p>
      </div>

      {formError && (
        <div className="payment-error">
          <span className="error-icon">⚠️</span>
          {formError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="chargify-form">
        <div className="form-row">
          <div className="form-field">
            <div id="chargify-first-name"></div>
          </div>
          <div className="form-field">
            <div id="chargify-last-name"></div>
          </div>
        </div>

        <div className="form-row form-row-card">
          <div className="form-field form-field-card">
            <div className="card-number-wrapper">
              <div id="chargify-card-number"></div>
              <div className="custom-card-icon">
                <svg width="32" height="20" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.5" y="0.5" width="31" height="19" rx="2.5" fill="#1a1a1a" stroke="rgba(255,255,255,0.3)"/>
                  <rect x="2" y="8" width="8" height="5" rx="1" fill="rgba(255,255,255,0.2)"/>
                  <line x1="12" y1="9" x2="18" y2="9" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
                  <line x1="12" y1="11" x2="16" y2="11" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <div id="chargify-month"></div>
          </div>
          <div className="form-field">
            <div id="chargify-year"></div>
          </div>
          <div className="form-field">
            <div id="chargify-cvv"></div>
          </div>
        </div>

        <div className="form-row form-row-zip">
          <div className="form-field form-field-zip">
            <div id="chargify-zip"></div>
          </div>
        </div>

        <div className="payment-security-note">
          <span className="lock-icon">🔒</span>
          <p>Your payment information is encrypted and secure. We never store your card details.</p>
        </div>

        <div className="payment-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn-cancel"
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-pay"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="btn-spinner"></span>
                {processingText}
              </>
            ) : (
              buttonText || (product?.price ? `Pay ${product.price}` : 'Save Card')
            )}
          </button>
        </div>
      </form>

      <div className="payment-test-cards">
        <details>
          <summary>Test Cards (Development Only)</summary>
          <ul>
            <li><strong>Success:</strong> 4111 1111 1111 1111</li>
            <li><strong>Decline:</strong> 4000 0000 0000 0002</li>
            <li><strong>CVV:</strong> Any 3 digits</li>
            <li><strong>Expiration:</strong> Any future date</li>
            <li><strong>ZIP:</strong> Any 5 digits</li>
          </ul>
        </details>
      </div>
    </div>
  );
};

export default ChargifyPaymentForm;

